import type { SimView } from "../../core/simView";
import {
  RELATION_ENEMY_RGB,
  RELATION_FRIEND_RGB,
  rgba,
} from "../../render/relationColors";
import type { RenderParams } from "../../sim/params";
import type { DrawContext, LensPlugin } from "../types";

/**
 * Draw directed relation edges with readability heuristics:
 * - Cap drawn count (stride) so n=400 doesn't become a solid slab
 * - Skip near-zero-length edges (self / coincident)
 * - Optional distance fade so long cross-camp bridges stay visible but soft
 * - Friend vs enemy different caps / widths
 */
function drawEdges(
  view: SimView,
  g: DrawContext,
  targets: Int32Array,
  rgb: readonly [number, number, number],
  width: number,
  baseOpacity: number,
  maxEdges: number,
): void {
  const { ctx, screenX, screenY, render } = g;
  const { n } = view;
  const stride = Math.max(1, Math.ceil(n / maxEdges));
  const plot = Math.max(0.5, render.plotRange);
  // Pixel length below which edge is noise
  const minPix2 = 4;
  ctx.lineWidth = Math.max(0.25, width);
  ctx.lineCap = "round";

  // Batch by opacity buckets to avoid per-edge strokeStyle thrash
  const buckets = 4;
  const paths: Path2D[] = [];
  for (let b = 0; b < buckets; b++) paths.push(new Path2D());

  for (let i = 0; i < n; i += stride) {
    const j = targets[i]!;
    if (j < 0 || j >= n || j === i) continue;
    const x0 = screenX[i]!;
    const y0 = screenY[i]!;
    const x1 = screenX[j]!;
    const y1 = screenY[j]!;
    const dx = x1 - x0;
    const dy = y1 - y0;
    const len2 = dx * dx + dy * dy;
    if (len2 < minPix2) continue;

    // Fade very long edges slightly (still visible)
    const len = Math.sqrt(len2);
    const scale = g.scale || 1;
    const worldApprox = len / Math.max(scale, 1e-6);
    const fade =
      worldApprox > plot * 1.2 ? 0.55 : worldApprox > plot * 0.7 ? 0.75 : 1;
    const b = Math.min(buckets - 1, Math.floor((1 - fade) * buckets));
    paths[b]!.moveTo(x0, y0);
    paths[b]!.lineTo(x1, y1);
  }

  for (let b = 0; b < buckets; b++) {
    const fade = 1 - ((b + 0.5) / buckets) * 0.5;
    const op = baseOpacity * fade;
    if (op < 0.02) continue;
    ctx.strokeStyle = rgba(rgb, op);
    ctx.stroke(paths[b]!);
  }
}

function edgeWidth(r: RenderParams): number {
  return r.edgeWidth ?? 1.5;
}

function edgeOpacity(r: RenderParams): number {
  return r.edgeOpacity ?? 0.45;
}

export const friendEdgesLens: LensPlugin = {
  id: "friend_edges",
  label: "Friend edges",
  tier: "basic",
  description: "Directed edges i → friend[i] (blue) — rings read as loops",
  enabled: (r) => r.showFriendEdges,
  draw(view: SimView, g: DrawContext) {
    // Friend rings: allow more edges (structure is sparse cycles)
    const max = view.types && view.types.length === view.n ? 800 : 2500;
    drawEdges(
      view,
      g,
      view.friends,
      RELATION_FRIEND_RGB,
      edgeWidth(g.render) * 1.15,
      Math.min(0.85, edgeOpacity(g.render) + 0.15),
      max,
    );
  },
};

export const enemyEdgesLens: LensPlugin = {
  id: "enemy_edges",
  label: "Enemy edges",
  tier: "basic",
  description: "Directed edges i → enemy[i] (amber) — cross-camp bridges",
  enabled: (r) => r.showEnemyEdges,
  draw(view: SimView, g: DrawContext) {
    // Enemy bridges denser visually — stricter cap + lower opacity
    const max = view.types && view.types.length === view.n ? 280 : 1800;
    drawEdges(
      view,
      g,
      view.enemies,
      RELATION_ENEMY_RGB,
      edgeWidth(g.render) * 0.85,
      Math.min(0.4, edgeOpacity(g.render) * 0.7),
      max,
    );
  },
};
